import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import DateRangeSelector from '../../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import TableActionsMenu from '../../../components/common/TableActionsMenu';
import { useDateSort } from '../../../hooks/useDateSort';
import apiClient from '../../../utils/axiosClient';

type RawItem = {
  date: string;
  retentionDays: number;
  cohortSize: number;
  userCount: number;
  cashUserCount: number;
};

type CohortRow = {
  date: string;
  cohortSize: number;
  userDays: Record<number, number>;
  cashDays: Record<number, number>;
};

function transformData(items: RawItem[]): { rows: CohortRow[]; maxDay: number } {
  const byDate: Record<string, CohortRow> = {};
  let maxDay = 0;

  for (const item of items) {
    if (!byDate[item.date]) {
      byDate[item.date] = { date: item.date, cohortSize: 0, userDays: {}, cashDays: {} };
    }
    if (item.retentionDays === 1) {
      byDate[item.date].cohortSize = item.cohortSize;
    }
    byDate[item.date].userDays[item.retentionDays] = item.userCount;
    byDate[item.date].cashDays[item.retentionDays] = item.cashUserCount;
    if (item.retentionDays > maxDay) maxDay = item.retentionDays;
  }

  return {
    rows: Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)),
    maxDay,
  };
}

function getRetentionBg(rate: number): string {
  if (rate <= 0) return 'transparent';
  if (rate <= 1) return '#EFF6FF';
  if (rate <= 4) return '#E0F2FE';
  if (rate <= 7) return '#DBEAFE';
  if (rate <= 10) return '#BFDBFE';
  if (rate <= 15) return '#93C5FD';
  return '#60A5FA';
}

const INNER_BOX_SX = {
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  borderRadius: 3,
  p: 2,
  mt: 3,
};

interface DashboardProps {
  rows: CohortRow[];
  maxDay: number;
  selectedApp: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
}

function WelcomeRetentionChart({ rows }: { rows: CohortRow[] }) {
  const firstRow = rows[0] ?? null;

  const chartData = useMemo(() => {
    if (!firstRow || firstRow.cohortSize === 0) return [];
    const maxDay = Math.max(...Object.keys(firstRow.userDays).map(Number));
    return Array.from({ length: maxDay }, (_, i) => i + 1)
      .filter((d) => firstRow.userDays[d] !== undefined)
      .map((d) => ({
        label: `D${d}`,
        접속: parseFloat(((firstRow.userDays[d] / firstRow.cohortSize) * 100).toFixed(2)),
        캐시: parseFloat((((firstRow.cashDays[d] ?? 0) / firstRow.cohortSize) * 100).toFixed(2)),
      }));
  }, [firstRow]);

  if (!firstRow || chartData.length === 0) return null;

  return (
    <Box sx={INNER_BOX_SX}>
      <Typography variant="h6" align="center" fontWeight="bold" sx={{ mb: 1 }}>
        잔존율 추이
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 1.5, fontSize: '0.82rem', color: '#4b5563' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 12, height: 2, backgroundColor: '#111827' }} />
          <span>방문 유저 수</span>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 12, height: 2, backgroundColor: '#2563eb' }} />
          <span>캐시 적립 유저 수</span>
        </Box>
      </Box>
      <Box sx={{ width: '100%', height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 24, left: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} interval="preserveStartEnd" />
            <YAxis
              domain={[0, 'auto']}
              tickFormatter={(v) => `${v}%`}
              tick={{ fill: '#6b7280', fontSize: 12 }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={false}
              width={48}
            />
            <RechartsTooltip
              formatter={(value: number, name: string) => [`${value.toFixed(2)}%`, name]}
              labelStyle={{ fontWeight: 600 }}
              contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13 }}
            />
            <Line type="monotone" dataKey="접속" stroke="#111827" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} isAnimationActive={false} />
            <Line type="monotone" dataKey="캐시" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

function WelcomeRetentionTable({ rows, maxDay, selectedApp, startDate, endDate }: DashboardProps) {
  const tableRef = useRef<HTMLTableElement>(null);
  const { sorted, sortDirection, toggleSort } = useDateSort(rows, undefined, 'asc');
  const dayColumns = useMemo(() => Array.from({ length: maxDay }, (_, i) => i + 1), [maxDay]);

  const filename = useMemo(() => {
    const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '_전체';
    return `웰컴_리텐션_${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
  }, [selectedApp, startDate, endDate]);

  const allRowsData = useMemo(() =>
    sorted.map((row) => [
      row.date,
      row.cohortSize.toLocaleString('ko-KR'),
      ...dayColumns.map((d) => {
        const count = row.userDays[d] ?? 0;
        const cashCount = row.cashDays[d] ?? 0;
        const rate = row.cohortSize > 0 ? (count / row.cohortSize) * 100 : 0;
        const cashRate = row.cohortSize > 0 ? (cashCount / row.cohortSize) * 100 : 0;
        return `${count.toLocaleString('ko-KR')} (${rate.toFixed(1)}%) / ${cashCount.toLocaleString('ko-KR')} (${cashRate.toFixed(1)}%)`;
      }),
    ]),
    [sorted, dayColumns]
  );

  return (
    <Box sx={INNER_BOX_SX}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: '0.85rem', color: '#4b5563' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '999px', backgroundColor: '#111827' }} />
            <Box component="span">방문 유저 수</Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '999px', backgroundColor: '#2563eb' }} />
            <Box component="span">캐시 적립 유저 수</Box>
          </Box>
        </Box>
        <TableActionsMenu tableRef={tableRef} allRowsData={allRowsData} filename={filename} sheetName="웰컴 리텐션" />
      </Box>

      <TableContainer sx={{ backgroundColor: '#fff', overflowX: 'auto' }}>
        <Table ref={tableRef} stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  코호트 날짜
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                신규 디바이스 유저 수
              </TableCell>
              {dayColumns.map((d) => (
                <TableCell key={d} align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  D{d}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.date}>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{row.date}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {row.cohortSize.toLocaleString('ko-KR')}
                </TableCell>
                {dayColumns.map((d) => {
                  const retentionDate = dayjs(row.date).add(d, 'day');
                  if (retentionDate.isAfter(endDate, 'day')) {
                    return <TableCell key={d} align="right" sx={{ color: '#d1d5db' }}>—</TableCell>;
                  }
                  const count = row.userDays[d] ?? 0;
                  const cashCount = row.cashDays[d] ?? 0;
                  const rate = row.cohortSize > 0 ? (count / row.cohortSize) * 100 : 0;
                  const cashRate = row.cohortSize > 0 ? (cashCount / row.cohortSize) * 100 : 0;
                  return (
                    <TableCell
                      key={d}
                      align="right"
                      sx={{
                        whiteSpace: 'nowrap',
                        backgroundColor: getRetentionBg(rate),
                        transition: 'background-color 0.2s ease-out',
                        py: 0.9,
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.3, fontSize: '0.9rem' }}>
                        <Box component="span" sx={{ lineHeight: 1.15, color: '#111827' }}>
                          {count.toLocaleString('ko-KR')} ({rate.toFixed(1)}%)
                        </Box>
                        <Box component="span" sx={{ fontSize: '0.8rem', color: '#2563eb' }}>
                          {cashCount.toLocaleString('ko-KR')} ({cashRate.toFixed(1)}%)
                        </Box>
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function WelcomeRetention() {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);

  const [rawData, setRawData] = useState<RawItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchFilterOptions = useCallback(() => {
    apiClient.get('/api/v1/stat/pointhome/app/list').then((res) => {
      setAppOptions(['앱 전체', ...res.data]);
    }).catch(() => undefined);
  }, []);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate || selectedApp === null) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get<RawItem[]>('/api/v1/analytics/pointhome/retention/welcome', {
        params: {
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD'),
          app: selectedApp === '앱 전체' ? 'all' : selectedApp,
        },
      });
      setRawData(res.data);
    } catch {
      setError('데이터 로드에 실패했습니다.');
      setRawData([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedApp]);

  useEffect(() => { fetchFilterOptions(); }, [fetchFilterOptions]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const { rows, maxDay } = useMemo(() => transformData(rawData), [rawData]);

  const content = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress size={40} sx={{ color: '#0044D2' }} />
          <Typography variant="body2" sx={{ ml: 2 }}>데이터 로딩 중...</Typography>
        </Box>
      );
    }
    if (error) {
      return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
    }
    if (rows.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          데이터가 없습니다.
        </Typography>
      );
    }
    return (
      <>
        <WelcomeRetentionChart rows={rows} />
        <WelcomeRetentionTable
          rows={rows}
          maxDay={maxDay}
          selectedApp={selectedApp}
          startDate={startDate ?? dayjs()}
          endDate={endDate ?? dayjs()}
        />
      </>
    );
  };

  return (
    <Box sx={{ opacity: loading ? 0.3 : 1, transition: 'opacity 0.3s ease' }}>
      {/* 필터 */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: { xs: 1.5, md: 0 } }}>
        <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </Box>
        <Box sx={{ ml: { xs: 0, md: 3 }, mt: { xs: 1.5, md: 0 }, width: { xs: '100%', md: 'auto' } }}>
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={setSelectedApp}
          />
        </Box>
      </Box>

      {/* 앱 chip + 콘텐츠 */}
      <Box sx={PAGE_CARD_SX}>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': { backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6' },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        {content()}
      </Box>
    </Box>
  );
}
